import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Send, CheckCircle, AlertTriangle, Paperclip, X } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { submitFeedback, type FeedbackType } from '../../services/feedbackApi'

const APP_VERSION = '1.0.0'

const TYPES: FeedbackType[] = ['bug', 'feature', 'question', 'other']
const MIN_LEN = 10
const MAX_LEN = 2000
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024
const ALLOWED_SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp']

interface FeedbackFormProps {
  actorId?: string
}

/**
 * Compact in-add-in feedback form for the test period.
 * POSTs to /api/v1/feedback/ — covers bug/feature/question/other.
 */
export function FeedbackForm({ actorId: _actorId }: FeedbackFormProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [type, setType] = useState<FeedbackType>('bug')
  const [message, setMessage] = useState('')
  const [contactEmail, setContactEmail] = useState(() => user?.email ?? '')
  const [screenshot, setScreenshot] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const reset = () => {
    setMessage('')
    setType('bug')
    setScreenshot(null)
    setError(null)
    setSubmitted(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const file = e.target.files?.[0]
    if (!file) {
      setScreenshot(null)
      return
    }
    if (!ALLOWED_SCREENSHOT_TYPES.includes(file.type)) {
      setError(t('feedback.errors.invalidImage'))
      e.target.value = ''
      return
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setError(t('feedback.errors.imageTooLarge'))
      e.target.value = ''
      return
    }
    setScreenshot(file)
  }

  const handleSubmit = async () => {
    setError(null)
    if (message.trim().length < MIN_LEN) {
      setError(t('feedback.errors.tooShort', { min: MIN_LEN }))
      return
    }
    if (message.length > MAX_LEN) {
      setError(t('feedback.errors.tooLong', { max: MAX_LEN }))
      return
    }

    setSubmitting(true)
    const result = await submitFeedback({
      type,
      message: message.trim(),
      contactEmail: contactEmail.trim() || undefined,
      appVersion: APP_VERSION,
      screenshot: screenshot ?? undefined,
    })
    setSubmitting(false)

    if (!result.success) {
      setError(result.error || t('feedback.errors.submit'))
      return
    }
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center py-4 text-center">
        <CheckCircle size={20} className="text-success-500 mb-2" />
        <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100">
          {t('feedback.thanksShort')}
        </p>
        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1 px-2">
          {t('feedback.thanksHint')}
        </p>
        <button
          onClick={reset}
          className="mt-2 text-[10px] text-primary-600 dark:text-primary-400 hover:underline"
        >
          {t('feedback.sendAnother')}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Type selector */}
      <div className="grid grid-cols-2 gap-1">
        {TYPES.map((t_opt) => (
          <button
            key={t_opt}
            type="button"
            onClick={() => setType(t_opt)}
            className={`py-1.5 text-[10px] rounded-md border transition-colors ${
              type === t_opt
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:border-neutral-300'
            }`}
          >
            {t(`feedback.types.${t_opt}`)}
          </button>
        ))}
      </div>

      {/* Message */}
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t('feedback.messagePlaceholder')}
        rows={3}
        maxLength={MAX_LEN}
        className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1 resize-none"
      />
      <div className="flex justify-between text-[9px] text-neutral-400">
        <span>{t('feedback.minHint', { min: MIN_LEN })}</span>
        <span>{message.length}/{MAX_LEN}</span>
      </div>

      {/* Contact email */}
      <input
        type="email"
        value={contactEmail}
        onChange={(e) => setContactEmail(e.target.value)}
        placeholder={t('feedback.emailPlaceholder')}
        className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
      />

      {/* Screenshot picker */}
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleScreenshotChange}
          className="hidden"
          id="feedback-screenshot-input"
        />
        <label
          htmlFor="feedback-screenshot-input"
          className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer"
        >
          <Paperclip size={10} />
          {screenshot ? t('feedback.screenshotChange') : t('feedback.screenshotAdd')}
        </label>
        {screenshot && (
          <>
            <span className="flex-1 truncate text-[10px] text-neutral-500" title={screenshot.name}>
              {screenshot.name}
            </span>
            <button
              type="button"
              onClick={() => {
                setScreenshot(null)
                if (fileInputRef.current) fileInputRef.current.value = ''
              }}
              className="text-neutral-400 hover:text-error-500"
              aria-label={t('feedback.screenshotRemove')}
            >
              <X size={12} />
            </button>
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-1 text-[10px] text-error-500">
          <AlertTriangle size={10} />
          {error}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting || message.trim().length < MIN_LEN}
        className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium transition-colors"
      >
        <Send size={12} />
        {submitting ? t('feedback.submitting') : t('feedback.submit')}
      </button>
    </div>
  )
}
